/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { Ref } from 'preact';
import type { UnsafeDomHandle } from '#utils/UNSAFE_unsafeDomAccess';
import type { ListViewportConfig } from '../List.types';

import { createRef } from 'preact';
import { useCallback, useImperativeHandle, useMemo, useState } from 'preact/hooks';
import { UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';
import { getViewportConfig } from '../utils/ListScrollUtils';

type UseSizingManagerProps = {
  viewportConfig?: ListViewportConfig;
  externalScrollerAccessRef?: Ref<UnsafeDomHandle>;
};

/**
 * Hook that manages sizing on the Preact TableList
 */
export function useSizingManager({
  viewportConfig,
  externalScrollerAccessRef
}: UseSizingManagerProps) {
  // scrollerRef is callback-based to meet useResizeObserver hook's state management requirements
  const [scrollerRef, setScrollerRef] = useState(createRef<HTMLElement>());
  const scrollerRefCallback = useCallback((node: HTMLDivElement | null) => {
    setScrollerRef({ current: node });
  }, []);

  useImperativeHandle(
    externalScrollerAccessRef!,
    () => {
      if (viewportConfig != null) {
        return { [UNSAFE_DOM_ACCESS]: viewportConfig.scrollerRef.current! };
      }
      return { [UNSAFE_DOM_ACCESS]: scrollerRef.current! };
    },
    [scrollerRef, viewportConfig]
  );

  // current viewport information
  const viewportInfo = useMemo(() => {
    const listViewportConfig = getViewportConfig(scrollerRef, viewportConfig);
    return {
      isExternalScroller: viewportConfig != null,
      viewportConfig: listViewportConfig
    };
  }, [scrollerRef, viewportConfig]);

  return { viewportInfo, scrollerRefCallback };
}
